import { useEffect, useRef, useCallback } from "react";
import { useACLContext, type CheckOptions } from "../context";
import { useCan } from "./useCan";
import type { Action, Resource, Role, User } from "@shield-acl/core";

type Cleanup = (() => void) | void;

/**
 * Sincroniza os grants do USUÁRIO ATUAL em tempo real.
 * Suporta push (a notificação traz o user) e pull (sinal → refetch → aplica).
 * A função `subscribe` roda uma vez (montagem) e retorna o unsubscribe.
 *
 * @example
 * // push
 * useUserSync((apply) => socket.on("acl:user", (m) => apply(m.user)))
 * // pull
 * useUserSync((apply) => socket.on("acl:invalidate", async () => apply(await api.me())))
 */
export function useUserSync(
  subscribe: (apply: (user: User | null) => void) => Cleanup,
): void {
  const { setUser } = useACLContext();
  const ref = useRef(subscribe);
  ref.current = subscribe;
  useEffect(() => {
    const cleanup = ref.current(setUser);
    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [setUser]);
}

/**
 * Sincroniza o CATÁLOGO DE ROLES (afeta todos que as têm).
 * `reload(roles)` chama `engine.setRoles` — 1 notificação, toda a árvore reavalia.
 *
 * @example
 * useRolesSync((reload) => socket.on("acl:roles", async () => reload(await api.roles())))
 */
export function useRolesSync(
  subscribe: (reload: (roles: Role[]) => void) => Cleanup,
): void {
  const { engine } = useACLContext();
  const reload = useCallback(
    (roles: Role[]) => engine.setRoles(roles),
    [engine],
  );
  const ref = useRef(subscribe);
  ref.current = subscribe;
  useEffect(() => {
    const cleanup = ref.current(reload);
    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [reload]);
}

/**
 * Reage ao GANHO/PERDA de uma permissão (ex.: ao perder acesso, redirecionar).
 */
export function usePermissionEffect(
  action: Action,
  resource: Resource | undefined,
  handlers: { onGain?: () => void; onLose?: () => void },
  opts?: CheckOptions,
): void {
  const allowed = useCan(action, resource, opts);
  const prev = useRef<boolean | null>(null);
  useEffect(() => {
    if (prev.current !== null) {
      if (allowed && !prev.current) handlers.onGain?.();
      else if (!allowed && prev.current) handlers.onLose?.();
    }
    prev.current = allowed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);
}
